import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Truck, Plus, Edit2, Trash2, Star, TrendingUp } from 'lucide-react';
import './VendorsTab.css';

const VendorsTab = ({ hotelId, token }) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    vendor_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    rating: 5
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (hotelId && token) {
      fetchVendors();
    }
  }, [hotelId, token]);

  const fetchVendors = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/inventory/vendors?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const vendorsData = Array.isArray(response.data?.data)
        ? response.data.data
        : Array.isArray(response.data)
          ? response.data
          : [];
      setVendors(vendorsData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching vendors:', err);
      setError('Failed to load vendors');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const payload = {
        ...formData,
        hotel_id: hotelId,
        rating: parseFloat(formData.rating)
      };

      if (editingVendor) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/inventory/vendors/${editingVendor.vendor_id}?hotel_id=${hotelId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Vendor updated successfully');
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/inventory/vendors?hotel_id=${hotelId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess('Vendor created successfully');
      }
      
      fetchVendors();
      resetForm();
    } catch (err) {
      console.error('Error saving vendor:', err);
      setError(err.response?.data?.message || 'Failed to save vendor');
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendor_name: vendor.vendor_name,
      contact_person: vendor.contact_person || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      address: vendor.address || '',
      rating: vendor.rating || 5
    });
    setShowForm(true);
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/inventory/vendors/${vendorId}?hotel_id=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess('Vendor deleted successfully');
      fetchVendors();
    } catch (err) {
      console.error('Error deleting vendor:', err);
      setError(err.response?.data?.message || 'Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      rating: 5
    });
    setEditingVendor(null);
    setShowForm(false);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star 
        key={i} 
        size={16} 
        fill={i < rating ? '#fbbf24' : 'none'}
        stroke={i < rating ? '#fbbf24' : '#d1d5db'}
      />
    ));
  };

  if (loading) {
    return (
      <div className="tab-loading">
        <div className="loading-spinner"></div>
        <p>Loading vendors...</p>
      </div>
    );
  }

  return (
    <div className="vendors-tab">
      <div className="tab-header">
        <h2><Truck size={24} /> Vendors</h2>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          <Plus size={20} /> {showForm ? 'Cancel' : 'Add Vendor'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {showForm && (
        <div className="form-container">
          <h3>{editingVendor ? 'Edit Vendor' : 'New Vendor'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name *</label>
                <input
                  type="text"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  required
                  maxLength={200}
                  placeholder="Company name"
                />
              </div>

              <div className="form-group">
                <label>Contact Person</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  maxLength={100}
                  placeholder="Contact person name"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  maxLength={100}
                  placeholder="vendor@email.com"
                />
              </div>

              <div className="form-group">
                <label>Phone</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  maxLength={20}
                  placeholder="+1234567890"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Address</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows={2}
                placeholder="Full address"
              />
            </div>

            <div className="form-group">
              <label>Rating (0-5)</label>
              <input
                type="number"
                step="0.5"
                min="0"
                max="5"
                value={formData.rating}
                onChange={(e) => setFormData({...formData, rating: e.target.value})}
                required
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingVendor ? 'Update' : 'Create'} Vendor
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="vendors-grid">
        {(Array.isArray(vendors) ? vendors : []).map((vendor) => (
          <div key={vendor.vendor_id} className="vendor-card">
            <div className="vendor-header">
              <Truck size={24} />
              <div className="vendor-actions">
                <button 
                  className="btn-icon" 
                  onClick={() => handleEdit(vendor)}
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button 
                  className="btn-icon btn-danger" 
                  onClick={() => handleDelete(vendor.vendor_id)}
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            
            <h3>{vendor.vendor_name}</h3>
            {vendor.contact_person && <p className="contact-person">Contact: {vendor.contact_person}</p>}
            
            <div className="vendor-details">
              {vendor.email && <div className="detail-item">📧 {vendor.email}</div>}
              {vendor.phone && <div className="detail-item">📞 {vendor.phone}</div>}
              {vendor.address && <div className="detail-item">📍 {vendor.address}</div>}
            </div>

            <div className="vendor-rating">
              {renderStars(vendor.rating)}
              <span className="rating-text">{vendor.rating}/5</span>
            </div>
          </div>
        ))}
      </div>

      {(Array.isArray(vendors) ? vendors : []).length === 0 && (
        <div className="empty-state">
          <Truck size={48} />
          <h3>No Vendors</h3>
          <p>Add vendor information to manage suppliers</p>
        </div>
      )}
    </div>
  );
};

export default VendorsTab;
